import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Plus, X } from 'lucide-react'
import {
  useReceiveStock,
  useWarehouses,
  type StockCondition,
  type StockPackaging,
  type UsedGrade,
} from '@/api/warehouse'
import { CatalogProductPicker, type CatalogPickerProduct } from '@/components/products/catalog-product-picker'
import { LoadingButton } from '@/components/loading-button'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { InlineFieldInput, InlineFieldTextarea } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { formatApiErrorMessage } from '@/lib/api-error'
import { CONDITION_OPTIONS, GRADE_OPTIONS, PACKAGING_OPTIONS, itemLink } from './model'

function today() {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The product being received, from the catalog picker or the item page it was opened from. */
export type ReceiveProduct = {
  uuid: string
  model: string
  store_title?: string | null
  manufacturer_name?: string | null
  image_url: string | null
  units_per_case: number
}

function fromCatalog(product: CatalogPickerProduct): ReceiveProduct {
  const multiplier = Number(product.price_multiplier ?? 1)
  const image = [...(product.images ?? [])].sort((a, b) => a.order - b.order).find((item) => !item.hidden)
  return {
    uuid: product.uuid,
    model: product.model,
    store_title: product.store_title,
    manufacturer_name: product.manufacturer_name,
    image_url: image ? image.public_url || image.source_url : null,
    units_per_case: Number.isFinite(multiplier) && multiplier > 1 ? Math.trunc(multiplier) : 1,
  }
}

type Place = { key: number; quantity: string; bay: string; pallet: string }

const firstPlace = (): Place => ({ key: 0, quantity: '1', bay: '', pallet: '' })

function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** Receive stock into the warehouse: pick the catalog product, then count, condition, place and Net. */
export function ReceiveStockSheet({
  open,
  onOpenChange,
  preset,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Opened from an item's page: its product and condition are already chosen. */
  preset?: { product: ReceiveProduct; condition: StockCondition; grade: UsedGrade | null }
}) {
  const phone = useMediaQuery(PHONE_QUERY)
  const navigate = useNavigate()
  const sheetRef = useRef<HTMLDivElement>(null)
  const warehouses = useWarehouses().data?.filter((warehouse) => warehouse.active) ?? []
  const receive = useReceiveStock()

  const [product, setProduct] = useState<ReceiveProduct | null>(preset?.product ?? null)
  const [warehouseUuid, setWarehouseUuid] = useState<string | null>(null)
  const [condition, setCondition] = useState<StockCondition | null>(preset?.condition ?? null)
  const [grade, setGrade] = useState<UsedGrade | null>(preset?.grade ?? null)
  const [places, setPlaces] = useState<Place[]>(() => [firstPlace()])
  const [receivedAt, setReceivedAt] = useState(today)
  const [unitNet, setUnitNet] = useState('')
  const [packaging, setPackaging] = useState<StockPackaging | null>(null)
  const [packingNotes, setPackingNotes] = useState('')
  const [tried, setTried] = useState(false)

  const [wasOpen, setWasOpen] = useState(open)
  // Every opening starts from the item it was opened from (or blank).
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open && preset) {
      setProduct(preset.product)
      setCondition(preset.condition)
      setGrade(preset.grade)
    }
  }

  const warehouse = warehouseUuid ?? warehouses[0]?.uuid ?? null
  const perCase = product?.units_per_case ?? 1
  const counts = places.map((place) => Number(place.quantity))
  const badCount = (count: number) => !Number.isInteger(count) || count < 1
  const total = counts.reduce((sum, count) => sum + (badCount(count) ? 0 : count), 0)
  const problems = {
    product: !product,
    condition: !condition,
    grade: condition === 'used' && !grade,
    quantity: counts.some(badCount),
    net: unitNet.trim() !== '' && !(Number(unitNet) >= 0),
  }
  const blocked = Object.values(problems).some(Boolean) || !warehouse

  const reset = () => {
    setProduct(null)
    setCondition(null)
    setGrade(null)
    setPlaces([firstPlace()])
    setReceivedAt(today())
    setUnitNet('')
    setPackaging(null)
    setPackingNotes('')
    setTried(false)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setTried(true)
    if (blocked || !product || !condition || !warehouse) return
    receive.mutate(
      {
        warehouse_uuid: warehouse,
        product_uuid: product.uuid,
        condition,
        used_grade: condition === 'used' ? grade : null,
        places: places.map((place) => ({
          quantity: Number(place.quantity),
          bay: place.bay.trim() || null,
          pallet: place.pallet.trim() || null,
        })),
        received_at: receivedAt,
        packaging,
        packing_notes: packingNotes.trim() || null,
        unit_net: unitNet.trim() === '' ? null : unitNet.trim(),
      },
      {
        onSuccess: (item) => {
          toast.success(`Received ${total} × ${product.model}`, {
            action: { label: 'Open', onClick: () => void navigate(itemLink(item)) },
          })
          reset()
          onOpenChange(false)
        },
        onError: (error) => toast.error(formatApiErrorMessage('Stock not received', error)),
      },
    )
  }

  const form = (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Field label="Product">
        {product ? (
          <div className="flex items-start gap-2 rounded-lg border p-2">
            <POLineItemProductCell
              model={product.model}
              description={product.store_title}
              productUuid={product.uuid}
              imageUrl={product.image_url}
              manufacturerName={product.manufacturer_name}
              compact
              collapseLongDescription
              collapsedDescriptionLines={1}
              className="min-w-0 flex-1"
            />
            {!preset && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                aria-label="Pick another product"
                onClick={() => setProduct(null)}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className={tried && problems.product ? 'rounded-md ring-1 ring-destructive/60' : undefined}>
            <CatalogProductPicker
              storeOnly={false}
              triggerLabel="Search the catalog"
              menuClassName="w-[var(--radix-dropdown-menu-trigger-width)]"
              onSelect={(picked) => setProduct(fromCatalog(picked))}
            />
          </div>
        )}
      </Field>

      <Field label="Condition">
        <ReviewChoiceGroup
          label="Condition"
          value={condition}
          options={CONDITION_OPTIONS}
          onChange={(next) => {
            setCondition(next)
            if (next !== 'used') setGrade(null)
          }}
          className={tried && problems.condition ? 'border-destructive/60' : undefined}
        />
      </Field>
      {condition === 'used' && (
        <Field label="Grade">
          <ReviewChoiceGroup
            label="Used grade"
            value={grade}
            options={GRADE_OPTIONS}
            onChange={setGrade}
            className={tried && problems.grade ? 'border-destructive/60' : undefined}
          />
        </Field>
      )}

      <PlacesEditor
        places={places}
        onChange={setPlaces}
        perCase={perCase}
        total={total}
        invalid={tried && problems.quantity}
      />

      <div className="grid grid-cols-2 gap-3">
        <Field label="Received" htmlFor="receive-date">
          <InlineFieldInput id="receive-date" type="date" value={receivedAt} onChange={(event) => setReceivedAt(event.target.value)} />
        </Field>
        <Field label="Net each" htmlFor="receive-net">
          <InlineFieldInput
            id="receive-net"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={unitNet}
            onChange={(event) => setUnitNet(event.target.value)}
            placeholder="What we paid"
            aria-invalid={(tried && problems.net) || undefined}
          />
        </Field>
        {warehouses.length > 1 && (
          <Field label="Warehouse">
            <ReviewChoiceGroup
              label="Warehouse"
              value={warehouse}
              options={warehouses.map((item) => ({ value: item.uuid, label: item.name }))}
              onChange={setWarehouseUuid}
            />
          </Field>
        )}
      </div>

      <Field label="Packaging">
        <ReviewChoiceGroup
          label="Packaging"
          value={packaging}
          options={PACKAGING_OPTIONS}
          onChange={(next) => setPackaging(next === packaging ? null : next)}
        />
      </Field>
      <Field label="Packing notes" htmlFor="receive-notes">
        <InlineFieldTextarea
          id="receive-notes"
          value={packingNotes}
          onChange={(event) => setPackingNotes(event.target.value)}
          placeholder="Damage, missing parts, how it is wrapped"
          className="min-h-16"
        />
      </Field>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <LoadingButton type="submit" loading={receive.isPending} loadingText="Receiving..." disabled={tried && blocked}>
          Receive
        </LoadingButton>
      </div>
    </form>
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={sheetRef}
        side={phone ? 'bottom' : 'right'}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          // No keyboard until a field is tapped.
          event.preventDefault()
          sheetRef.current?.focus()
        }}
        className={
          phone
            ? 'max-h-[92dvh] gap-0 overflow-y-auto rounded-t-xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 outline-none'
            : 'w-full gap-0 overflow-y-auto p-6 outline-none sm:max-w-md'
        }
      >
        <SheetTitle className="pb-4 pr-10 text-base font-semibold">Receive stock</SheetTitle>
        {form}
      </SheetContent>
    </Sheet>
  )
}

/** Where the delivery went: one row per bay/pallet with how many went there. */
function PlacesEditor({
  places,
  onChange,
  perCase,
  total,
  invalid,
}: {
  places: Place[]
  onChange: (places: Place[]) => void
  perCase: number
  total: number
  invalid: boolean
}) {
  const update = (key: number, change: Partial<Place>) =>
    onChange(places.map((place) => (place.key === key ? { ...place, ...change } : place)))
  const several = places.length > 1
  return (
    <fieldset className="space-y-1.5">
      <legend className="sr-only">Where it went</legend>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem_2rem] gap-x-2 text-xs font-medium text-muted-foreground">
        <span>Bay</span>
        <span>Pallet</span>
        <span>{perCase > 1 ? 'Units' : 'Quantity'}</span>
      </div>
      {places.map((place, index) => (
        <div key={place.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5.5rem_2rem] items-center gap-x-2">
          <InlineFieldInput
            aria-label={`Bay ${index + 1}`}
            value={place.bay}
            onChange={(event) => update(place.key, { bay: event.target.value })}
            placeholder="B4"
          />
          <InlineFieldInput
            aria-label={`Pallet ${index + 1}`}
            value={place.pallet}
            onChange={(event) => update(place.key, { pallet: event.target.value })}
            placeholder="P12"
          />
          <InlineFieldInput
            aria-label={`How many at place ${index + 1}`}
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={place.quantity}
            onChange={(event) => update(place.key, { quantity: event.target.value })}
            aria-invalid={(invalid && !(Number(place.quantity) >= 1)) || undefined}
            className="tabular-nums"
          />
          {several ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground"
              aria-label={`Remove place ${index + 1}`}
              onClick={() => onChange(places.filter((other) => other.key !== place.key))}
            >
              <X className="size-4" />
            </Button>
          ) : (
            <span />
          )}
        </div>
      ))}
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 h-8 gap-1 px-2 text-muted-foreground"
          onClick={() =>
            onChange([...places, { key: Math.max(...places.map((place) => place.key)) + 1, quantity: '1', bay: '', pallet: '' }])
          }
        >
          <Plus className="size-3.5" aria-hidden /> Another place
        </Button>
        <p className="text-xs text-muted-foreground tabular-nums">
          {[several ? `${total} in ${places.length} places` : null, perCase > 1 ? `Case of ${perCase}` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </fieldset>
  )
}
